import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
  Switch,
  Checkbox,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { FaNotEqual } from "react-icons/fa";
import React, { useState, useEffect, useMemo } from "react";
import api from "../Api";
import { groupsFromAccounts, groupMatchesSelection, exportUnscannedCsv } from "../utils/scanGroups";

export default function NsDialog({
  open,
  onClose,
  selectedDate,
  accounts,
  selectedNsAccount,
  setSelectedNsAccount,
  fetchUnscannedOrders,
  nsOrderData,
  showAllAccounts,
  setShowAllAccounts,
  initialGroup = "",
}) {
  const [hideZeroCarry, setHideZeroCarry] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [openingPdf, setOpeningPdf] = useState(false);

  const groupOptions = groupsFromAccounts(accounts);

  const fetchGroup = (group) => {
    setSelectedNsAccount("");
    setSelectedGroup(group);
    setShowAllAccounts(true);
    fetchUnscannedOrders("all");
  };

  // Opened from a summary "remaining" click → auto-load that group.
  useEffect(() => {
    if (open && initialGroup) {
      fetchGroup(initialGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialGroup, selectedDate]);

  // Filter logic for "All Accounts" view: hide groups with 0 orders,
  // and narrow to the selected group when one is chosen.
  let filteredNsOrderData = nsOrderData;
  if (showAllAccounts && selectedGroup) {
    filteredNsOrderData = filteredNsOrderData.filter(
      (group) => groupMatchesSelection(group.account_name, selectedGroup)
    );
  }
  if (showAllAccounts && hideZeroCarry) {
    filteredNsOrderData = filteredNsOrderData.filter(
      (group) => group.orders && group.orders.length > 0
    );
  }

  const visibleRows = useMemo(() => {
    const rows = showAllAccounts
      ? filteredNsOrderData.flatMap((g) => (g.orders || []).map((o) => ({
          tracking_id: o.tracking_id,
          order_id: o.order_id,
          order_item_id: o.order_item_id,
          sku: o.sku,
          account_name: g.account_name,
        })))
      : filteredNsOrderData.map((o) => ({
          tracking_id: o.tracking_id,
          order_id: o.order_id,
          order_item_id: o.order_item_id,
          sku: o.sku,
          account_name: selectedNsAccount,
        }));
    return rows.filter((row) => row.tracking_id && row.account_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredNsOrderData, selectedNsAccount, showAllAccounts]);

  const visibleIds = visibleRows.map((row) => row.tracking_id);
  const pickedRows = visibleRows.filter((row) => picked.has(row.tracking_id));

  useEffect(() => { setPicked(new Set()); }, [nsOrderData, selectedGroup]);

  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => picked.has(id));
  const toggleAll = () => setPicked(allChecked ? new Set() : new Set(visibleIds));
  const toggleOne = (id) => setPicked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const openSelectedPdfs = async () => {
    const ids = [...new Set(pickedRows.map((row) => row.tracking_id))];
    if (!ids.length) { alert("Tick at least one order to open its label."); return; }
    setOpeningPdf(true);
    try {
      const resp = await api.post("/api/label-pdf",
        {
          tracking_ids: ids,
          date: selectedDate,
          items: pickedRows.map((row) => ({
            id: row.tracking_id,
            tracking_id: row.tracking_id,
            account: row.account_name,
            order_id: row.order_id || "",
            order_item_id: row.order_item_id || "",
            sku: row.sku || "",
          })),
        }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let msg = "Failed to open labels.";
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) {}
      alert("❌ " + msg);
    } finally {
      setOpeningPdf(false);
    }
  };

  return (
    <>
      {/* N/S DIALOG */}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="body2" sx={{ color: "gray" }}>
            📅 Date: {selectedDate}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: "#333",
              }}
            >
              🕵️‍♂️ <FaNotEqual /> SCAN
            </Typography>

            <Typography variant="body2" sx={{ color: "#666" }}>
              📦{" "}
              {showAllAccounts
                ? filteredNsOrderData.reduce(
                    (acc, g) => acc + (g.orders?.length || 0),
                    0
                  )
                : filteredNsOrderData.length}{" "}
              entries
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* ACCOUNT + GROUP SELECT */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl margin="normal" sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel>Select Account</InputLabel>
              <Select
                value={selectedNsAccount}
                label="Select Account"
                onChange={(e) => { setSelectedNsAccount(e.target.value); setSelectedGroup(""); }}
              >
                <MenuItem value="">-- Select Account --</MenuItem>
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={a.name}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl margin="normal" sx={{ minWidth: 160 }}>
              <InputLabel>Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Group"
                onChange={(e) => {
                  const g = e.target.value;
                  if (g) fetchGroup(g);
                  else setSelectedGroup("");
                }}
              >
                <MenuItem value="">-- All Groups --</MenuItem>
                {groupOptions.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ACTION BUTTONS + ZERO CARRY FILTER */}
          <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => {
                setShowAllAccounts(false);
                setSelectedGroup("");
                fetchUnscannedOrders(selectedNsAccount);
              }}
            >
              Fetch Orders
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setShowAllAccounts(true);
                setSelectedGroup("");
                fetchUnscannedOrders("all");
              }}
            >
              All Accounts
            </Button>

            <Button
              variant="contained"
              color="info"
              startIcon={<PictureAsPdfIcon />}
              disabled={picked.size === 0 || openingPdf}
              onClick={openSelectedPdfs}
            >
              {openingPdf ? "Opening…" : `Open Selected (${picked.size})`}
            </Button>

            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              disabled={filteredNsOrderData.length === 0}
              onClick={() => exportUnscannedCsv({
                nsOrderData: filteredNsOrderData,
                showAllAccounts,
                accountName: selectedNsAccount,
                fileLabel: selectedGroup || selectedNsAccount || "all",
                date: selectedDate,
              })}
            >
              Excel
            </Button>

            {/* Zero Carry Filter */}
            <FormControlLabel
              control={
                <Switch
                  checked={hideZeroCarry}
                  onChange={(e) => setHideZeroCarry(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Hide 0 Orders
                </Typography>
              }
              sx={{ ml: "auto" }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* TABLE */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={picked.size > 0 && !allChecked}
                      onChange={toggleAll}
                      disabled={visibleIds.length === 0}
                    />
                  </TableCell>
                  <TableCell>
                    <b>Tracking ID</b>
                  </TableCell>
                  <TableCell>
                    <b>SKU</b>
                  </TableCell>
                  <TableCell>
                    <b>Quantity</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredNsOrderData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No unscanned orders available.
                    </TableCell>
                  </TableRow>
                )}

                {!showAllAccounts &&
                  filteredNsOrderData.map((o, i) => (
                    <TableRow key={i} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={picked.has(o.tracking_id)}
                          onChange={() => toggleOne(o.tracking_id)} />
                      </TableCell>
                      <TableCell>{o.tracking_id}</TableCell>
                      <TableCell>{o.sku}</TableCell>
                      <TableCell>{o.quantity}</TableCell>
                    </TableRow>
                  ))}

                {showAllAccounts &&
                  filteredNsOrderData.map((group, i) => (
                    <React.Fragment key={i}>
                      {/* Account header row */}
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            backgroundColor: "#f0f0f0",
                            fontWeight: "bold",
                          }}
                        >
                          {group.account_name} ({group.orders?.length || 0})
                        </TableCell>
                      </TableRow>

                      {/* Orders */}
                      {group.orders?.map((o, j) => (
                        <TableRow key={`${i}-${j}`} hover>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={picked.has(o.tracking_id)}
                              onChange={() => toggleOne(o.tracking_id)} />
                          </TableCell>
                          <TableCell>{o.tracking_id}</TableCell>
                          <TableCell>{o.sku}</TableCell>
                          <TableCell>{o.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}

              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
